import { useCallback, useEffect, useRef, useState } from "react";

import {
  FitScreen as FitScreenIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from "@mui/icons-material";
import { Box, Button, Divider, IconButton, Stack, Tooltip } from "@mui/material";
import PropTypes from "prop-types";

import { getReportOverlayContainer } from "./fullscreen";
import { ReportDocumentFrame } from "./report-document-frame";

const ZOOM_STEPS = [0.5, 0.67, 0.75, 0.9, 1, 1.1, 1.25, 1.5];
const MIN_ZOOM = 0.3;
const MAX_ZOOM = 1.5;
// Fitting only ever shrinks the sheet, like a print preview; zoom in explicitly to enlarge.
const MAX_FIT_ZOOM = 1;
const PX_PER_MM = 96 / 25.4;
// The document's grey margin around the sheet (16px each side) plus its scrollbar.
const SHEET_GUTTER_PX = 48;
// Fills the screen below the app bar, the filter summary and this toolbar.
const VIEWPORT_HEIGHT = "max(420px, calc(100vh - 240px))";

const TYPING_TARGETS =
  'input, textarea, select, [contenteditable]:not([contenteditable="false"])';
// Keys pressed inside these belong to them (menu type-ahead, dialog fields).
const KEY_OWNING_LAYERS = '[role="dialog"], [role="menu"], [role="listbox"]';
// An open MUI dialog, menu or popover. Closed ones kept mounted (like the app shell's
// mobile drawer, which has role="dialog") carry MuiModal-hidden and do not count.
const OPEN_OVERLAYS = ".MuiModal-root:not(.MuiModal-hidden)";

const clampZoom = (value) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

/**
 * A document viewer around the sandboxed report frame. Zoom scales the frame element
 * from outside, so it needs no access to the (opaque-origin) document inside. With the
 * sheet width known, "Pas lebar" scales the whole sheet to the viewer width.
 */
export const ReportDocumentViewer = ({ html, title, info, actions, pageWidthMm }) => {
  const containerRef = useRef(null);
  const viewportRef = useRef(null);
  const canFit = pageWidthMm > 0;
  const [zoomMode, setZoomMode] = useState(canFit ? "fit" : "manual");
  const [manualZoom, setManualZoom] = useState(1);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const fitZoom =
    canFit && viewportWidth > 0
      ? clampZoom(
          Math.min(MAX_FIT_ZOOM, viewportWidth / (pageWidthMm * PX_PER_MM + SHEET_GUTTER_PX))
        )
      : 1;
  const isFitting = canFit && zoomMode === "fit";
  const zoom = isFitting ? fitZoom : manualZoom;

  useEffect(() => {
    const element = viewportRef.current;
    if (!element || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) =>
      setViewportWidth(entry.contentRect.width)
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const stepZoom = (direction) => {
    const steps = direction > 0 ? ZOOM_STEPS : [...ZOOM_STEPS].reverse();
    const next = steps.find((step) =>
      direction > 0 ? step > zoom + 0.001 : step < zoom - 0.001
    );
    setManualZoom(next ?? (direction > 0 ? MAX_ZOOM : ZOOM_STEPS[0]));
    setZoomMode("manual");
  };

  useEffect(() => {
    const handleChange = () =>
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    document.addEventListener("fullscreenchange", handleChange);
    return () => document.removeEventListener("fullscreenchange", handleChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement === containerRef.current) {
      document.exitFullscreen?.();
    } else {
      containerRef.current?.requestFullscreen?.();
    }
  }, []);

  // "F" toggles fullscreen, like a video player; Esc (handled by the browser) exits.
  // Keys typed inside the sandboxed report frame never reach this page.
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== "f" && event.key !== "F") return;
      if (event.defaultPrevented || event.repeat) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest(TYPING_TARGETS) || target?.closest(KEY_OWNING_LAYERS)) return;
      if (document.querySelector(OPEN_OVERLAYS)) return;
      event.preventDefault();
      toggleFullscreen();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [toggleFullscreen]);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        border: 1,
        borderColor: "divider",
        borderRadius: isFullscreen ? 0 : 1,
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction="row"
        spacing={0.5}
        useFlexGap
        sx={{ alignItems: "center", flexWrap: "wrap", px: 1, py: 0.5 }}
      >
        <Tooltip title="Perkecil">
          <span>
            <IconButton
              size="small"
              aria-label="Perkecil"
              disabled={zoom <= ZOOM_STEPS[0] + 0.001}
              onClick={() => stepZoom(-1)}
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Kembali ke 100%">
          <Button
            size="small"
            color="inherit"
            onClick={() => {
              setManualZoom(1);
              setZoomMode("manual");
            }}
            sx={{ minWidth: 56, fontVariantNumeric: "tabular-nums" }}
          >
            {Math.round(zoom * 100)}%
          </Button>
        </Tooltip>
        <Tooltip title="Perbesar">
          <span>
            <IconButton
              size="small"
              aria-label="Perbesar"
              disabled={zoom >= MAX_ZOOM - 0.001}
              onClick={() => stepZoom(1)}
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {canFit && (
          <Button
            size="small"
            color={isFitting ? "primary" : "inherit"}
            variant={isFitting ? "outlined" : "text"}
            startIcon={<FitScreenIcon fontSize="small" />}
            onClick={() => setZoomMode("fit")}
          >
            Pas lebar
          </Button>
        )}
        {info && <Box sx={{ ml: 1, minWidth: 0 }}>{info}</Box>}
        <Box sx={{ flex: 1 }} />
        {actions}
        <Tooltip
          title={isFullscreen ? "Keluar dari layar penuh (F)" : "Layar penuh (F)"}
          slotProps={{ popper: { container: getReportOverlayContainer } }}
        >
          <IconButton
            size="small"
            aria-label={isFullscreen ? "Keluar dari layar penuh" : "Layar penuh"}
            onClick={toggleFullscreen}
          >
            {isFullscreen ? (
              <FullscreenExitIcon fontSize="small" />
            ) : (
              <FullscreenIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Stack>
      <Divider />
      <Box
        ref={viewportRef}
        sx={{
          height: isFullscreen ? undefined : VIEWPORT_HEIGHT,
          flex: isFullscreen ? 1 : "none",
          minHeight: 0,
          overflow: "hidden",
          bgcolor: "#e5e7eb",
        }}
      >
        <ReportDocumentFrame
          html={html}
          title={title}
          sx={{
            width: `${100 / zoom}%`,
            height: `${100 / zoom}%`,
            transform: `scale(${zoom})`,
            transformOrigin: "top left",
            border: 0,
            borderRadius: 0,
          }}
        />
      </Box>
    </Box>
  );
};

ReportDocumentViewer.propTypes = {
  html: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  info: PropTypes.node,
  actions: PropTypes.node,
  pageWidthMm: PropTypes.number,
};
